import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Button } from "@nexus/ui";
import {
  useSendAlertsSlackTest,
  useSendChangelogSlackTest,
} from "@/features/changelog/hooks/useChangelogActions";
import { useChangelogPermissions } from "@/features/changelog/hooks/useChangelogPermissions";
import { changelogErrorMessage } from "@/features/changelog/lib/changelog-errors";
import { SettingRow, SettingSwitch, SettingsCard } from "../shared/SettingsCard";
import type { ChangelogSettingsCardProps } from "./types";

/** How new releases are noticed, and who hears about it when the pipeline breaks. */
export function SyncingAlertsCard({
  draft,
  set,
  isReadOnly,
  isSlackConfigured,
  isAlertsSlackConfigured,
}: ChangelogSettingsCardProps) {
  const { t } = useTranslation();
  const permissions = useChangelogPermissions();
  const slackTest = useSendChangelogSlackTest();
  const alertsSlackTest = useSendAlertsSlackTest();

  function handleSlackTest() {
    slackTest.mutate(undefined, {
      onSuccess: () =>
        toast.success(
          t("admin.changelog.settings.slackTestSent", "Test message sent — check the channel"),
        ),
    });
  }

  function handleAlertsSlackTest() {
    alertsSlackTest.mutate(undefined, {
      onSuccess: () =>
        toast.success(
          t("admin.changelog.settings.slackTestSent", "Test message sent — check the channel"),
        ),
    });
  }

  return (
    <SettingsCard
      icon="refresh-cw"
      title={t("admin.changelog.settings.ops.title", "Syncing and alerts")}
      description={t(
        "admin.changelog.settings.ops.description",
        "How new releases are noticed, and who hears about it when the pipeline breaks.",
      )}
    >
      <SettingRow
        htmlFor="auto-sync"
        label={t("admin.changelog.settings.autoSync", "Check GitHub when the tab opens")}
        description={t(
          "admin.changelog.settings.autoSyncHelp",
          "Saves a click at the cost of a release lookup each visit. Off, the Check for releases button is the only trigger.",
        )}
        control={
          <SettingSwitch
            id="auto-sync"
            checked={draft.autoSyncOnOpen}
            disabled={isReadOnly}
            onCheckedChange={(checked) => set("autoSyncOnOpen", checked)}
          />
        }
      />

      <SettingRow
        htmlFor="notify-failure"
        label={t("admin.changelog.settings.notifyOnFailure", "Post to Slack when generation fails")}
        description={t(
          "admin.changelog.settings.notifyOnFailureHelp",
          "Posts to the admin-alerts channel. A failed release note is silent otherwise — the entry simply never leaves Draft.",
        )}
        control={
          <SettingSwitch
            id="notify-failure"
            checked={draft.notifyOnFailure}
            disabled={isReadOnly}
            onCheckedChange={(checked) => set("notifyOnFailure", checked)}
          />
        }
      />

      {permissions.canTestSlack ? (
        <SettingRow
          label={t("admin.changelog.settings.slackTest", "Test the release-notes channel")}
          description={
            slackTest.error
              ? changelogErrorMessage(
                  slackTest.error,
                  t(
                    "admin.changelog.settings.slackTestError",
                    "The test message did not go through.",
                  ),
                )
              : t(
                  "admin.changelog.settings.slackTestHelp",
                  "Posts one throwaway message to the release-notes channel, so you know the webhook works before trusting it with a release.",
                )
          }
          control={
            <Button
              variant="outline"
              size="sm"
              className="h-8 rounded-sm text-xs"
              // Both buttons read "Send test", so the channel each one reaches
              // lives in the accessible name rather than only in the row label.
              aria-label={t(
                "admin.changelog.settings.slackTestButtonLabel",
                "Send test to the release-notes channel",
              )}
              disabled={!isSlackConfigured || slackTest.isPending}
              onClick={handleSlackTest}
            >
              {slackTest.isPending
                ? t("admin.changelog.settings.slackTestSending", "Sending…")
                : t("admin.changelog.settings.slackTestButton", "Send test")}
            </Button>
          }
        />
      ) : null}

      {/* A second button rather than a second use of the one above: the alert
          switch on this card posts to admin-alerts, and a release-notes webhook
          that answers proves nothing about that channel. */}
      {permissions.canTestAlertsSlack ? (
        <SettingRow
          label={t("admin.changelog.settings.alertsSlackTest", "Test the admin-alerts channel")}
          description={
            alertsSlackTest.error
              ? changelogErrorMessage(
                  alertsSlackTest.error,
                  t(
                    "admin.changelog.settings.alertsSlackTestError",
                    "The test message did not go through.",
                  ),
                )
              : t(
                  "admin.changelog.settings.alertsSlackTestHelp",
                  "Posts one throwaway message to the admin-alerts channel — where the failure alert above and every ops alert go, which is a different webhook to the release-notes one.",
                )
          }
          control={
            <Button
              variant="outline"
              size="sm"
              className="h-8 rounded-sm text-xs"
              aria-label={t(
                "admin.changelog.settings.alertsSlackTestButtonLabel",
                "Send test to the admin-alerts channel",
              )}
              disabled={!isAlertsSlackConfigured || alertsSlackTest.isPending}
              onClick={handleAlertsSlackTest}
            >
              {alertsSlackTest.isPending
                ? t("admin.changelog.settings.slackTestSending", "Sending…")
                : t("admin.changelog.settings.slackTestButton", "Send test")}
            </Button>
          }
        />
      ) : null}
    </SettingsCard>
  );
}
